import InputText from './input-text.js';
import {html} from 'lit';
import styles from './input-int.css' assert {type: 'css'};

/**
 * @element inv-input-int
 *
 * @class InputInt
 * @classdesc InputInt component that extends InputText and adds number specific functionality
 * @extends {InputText}
 */

export default class InputInt extends InputText {
    static #thousandSeparator = null;
    static #decimalSeparator = null;
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keydown', this.saveKeyPressed);
        this.inputMode = 'numeric';
        this.keyPressed = null;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', this.saveKeyPressed);
    }

    getStyleSheets() {
        return [...super.getStyleSheets(), styles];
    }

    /**
     * devuelve el carácter usado como separador de decimales
     *
     *
     * @return {string|null}
     */
    static get decimalSeparator() {
        if(this.#decimalSeparator === null) {
            const numberWithDecimalSeparator = 1.1;
            this.#decimalSeparator = Intl.NumberFormat().formatToParts(numberWithDecimalSeparator).find((part) => part.type === 'decimal')
                .value;
        }
        return this.#decimalSeparator;
    }

    /**
     * Devuelve el número de puntos antes de una posición en una cadena.
     *
     * @param {string} value - La cadena de entrada.
     * @param {number} position - La posición en la cadena.
     * @return {number} - El número de puntos antes de la posición.
     */
    numberOfDotsBeforePosition(value, position) {
        const thousandSeparator = this.constructor.thousandSeparator;
        let count = 0;

        if (value && position > 0) {
            for (let i = 0; i < position; i++) {
                if (value.charAt(i) === thousandSeparator) {
                    count++;
                }
            }
        }

        return count;
    }

    /**
     * devuelve el carácter usado como separador de miles
     *
     *
     * @return {string|null}
     */
    static get thousandSeparator() {
        if (this.#thousandSeparator === null) {
            const numberWithDecimalSeparator = 100000;
            this.#thousandSeparator = Intl.NumberFormat().formatToParts(numberWithDecimalSeparator).find((part) => part.type === 'group')
                .value;
        }
        return this.#thousandSeparator;
    }

    /**
     * devuelve un número a partir de una cadena formateada con los thousand separator and decimal separator
     *
     * @param {string} number - número que queremos parsear
     *
     * @returns {number} - número parseado
     */
    static parse (number)  {
        const value = parseFloat(number.replaceAll(this.thousandSeparator, '').replaceAll(this.decimalSeparator, '.'));
        return isNaN(value) ? null : value;
    }


    /**
     * devuelve un numero formateado con los thousand separator and decimal separator
     *
     * @param {string}  number   número que queremos tener formateado que se introduce por el input
     *
     * @return {string|null}
     */
    static format (number) {
        const thousandSeparator = this.thousandSeparator;
        const value = this.parse(number);
        return (isNaN(value) || value === null)
            ? null
            : value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

    }

    get #input() {
        return this.renderRoot?.querySelector(`input[name=${this.name}]`);
    }

    /**
     * Guardar la ultima tecla pulsada para reposicionar correctamente el cursor
     *
     * @param {KeyboardEvent} e
     *
     * @return {void}
     */

    saveKeyPressed = (e) => {
        this.keyPressed = e.key
    };

    handleEvent (e, eventName) {
        const thousandSeparator = this.constructor.thousandSeparator;
        let emittedValue = null;
        const input = this.#input;
        if(input){
            const inputValue = e.target.value;
            emittedValue = this.constructor.parse(inputValue);
            if (eventName === 'change') {
                const caretPosition = input.selectionStart;
                const newInputValue = this.constructor.format(inputValue);
                input.value = newInputValue;

                const newNumberOfDots = this.numberOfDotsBeforePosition(newInputValue, caretPosition);
                const oldNumberOfDots = this.numberOfDotsBeforePosition(inputValue, caretPosition);

                if (oldNumberOfDots !== newNumberOfDots) {
                    const adjustment = oldNumberOfDots < newNumberOfDots ? 1 : -1;
                    input.setSelectionRange(caretPosition + adjustment, caretPosition + adjustment);
                } else if (this.keyPressed === 'Delete' && newInputValue?.[caretPosition] === thousandSeparator) {
                    input.setSelectionRange(caretPosition + 1, caretPosition + 1);
                } else {
                    input.setSelectionRange(caretPosition, caretPosition);
                }
            }
        }
        this.emit(eventName, { value: emittedValue, name: this.name });
    }
    onFocusOut = (e) => this.handleEvent(e, 'focus-out');

    onFocusIn = (e) => this.handleEvent(e, 'focus-in');

    onChange = (e) => this.handleEvent(e, 'change');

    renderInput(childNodes) {
        return html`
            <div class="input-container ${this.getCss(['error', 'disabled'])}">
                <input
                    @input="${this.buildHandler('change')}"
                    @focusin="${this.buildHandler('focus-in')}"
                    @focusout="${this.buildHandler('focus-out')}"
                    .value="${this.constructor.format(this.value)}"
                    class="${this.getCss('error')}"
                    type="text"
                    id="${this.getId()}"
                    name="${this.name}"
                    placeholder="${this.placeholder}"
                    ?disabled="${this.disabled}"
                    inputmode="${this.inputMode}"
                />
                ${childNodes}
            </div>
        `;
    }
}

InputInt.register();
